import {
    GoogleAnalyticsConsumer,
    InteractionGatewayAnalyticsConsumer,
    PrometheusAnalyticsConsumer,
    TypeSafeAnalyticsWrapper,
} from '@sparx/client-analytics';
import bowser from 'bowser';
import { getClientVersion } from 'phoenix2/utils/versions.js';

export const analyticsConfigDefaults = {
    useGa: false,
    gaTimings: false,
    useWs: false,
    pushInterval: 5000,
    interactionGatewayPushInterval: 5000,
    interactionGatewayMaxEventsStorage: 1000,
    prometheusMaxEventsStorage: 1000,
};

const PROMETHUS_METRICS_QUEUE = 'metricQueue';
const INTERACTION_GATEWAY_METRICS_QUEUE = 'uiMetricQueue';

const SPARXWEB_CUSTOM_DIMENSIONS = [
    'userId',
    'version',
    'timestamp',
    'sessionId',
    'schoolName',
    'connectionId',
    'eventIndex',
];

/**
 * Instantiate an Analytics object with the supplied config, initialise in a
 * manner appropriate way for SW client. As SW client is not typesafe, it
 * returns a type wrapped Analytics object.
 * @param config
 * @param schoolId
 * @param application - the name of the application
 * @param onError - function to call when the analytics system needs to report a client error
 * @param clientVersion - the current version of the client
 * @returns {TypeSafeAnalyticsWrapper}
 */
export default function initAnalytics(config, schoolId, application, onError, clientVersion) {
    const analyticsConfig = Object.assign({}, analyticsConfigDefaults, config || {});
    const analytics = new TypeSafeAnalyticsWrapper();

    // Send analytics to Google Analytics

    if (analyticsConfig.useGa && settings.analyticsPropertyID && cookiePreferences.GA) {
        analytics.addConsumer(
            new GoogleAnalyticsConsumer(
                gtag,
                settings.analyticsPropertyID,
                analyticsConfig.gaTimings,
                SPARXWEB_CUSTOM_DIMENSIONS,
                // Exclude the monitor robots from sending GA data
                ['4601a870-9412-11e8-bb94-abeff25ea0a1', 'f768d26a-20b5-11ea-9b2f-837b04f470d0'],
                // exclude timing, analytics and pageView events
                {
                    timings: true,
                    events: true,
                    pageViews: true,
                    exceptions: false,
                },
            ),
        );
    }

    // Send analytics to Prometheus (via ClientGateway)
    if (analyticsConfig.pushUrl) {
        analytics.addConsumer(
            new PrometheusAnalyticsConsumer(
                analyticsConfig.pushUrl,
                analyticsConfig.pushInterval,
                PROMETHUS_METRICS_QUEUE,
                analyticsConfig.prometheusMaxEventsStorage,
                onError,
            ),
        );
    }

    // Send analytics to BigQuery (via Interaction Gateway)
    if (analyticsConfig.interactionGatewayUrl) {
        analytics.addConsumer(
            new InteractionGatewayAnalyticsConsumer(
                analyticsConfig.interactionGatewayUrl,
                analyticsConfig.interactionGatewayPushInterval,
                INTERACTION_GATEWAY_METRICS_QUEUE,
                analyticsConfig.interactionGatewayMaxEventsStorage,
                onError,
            ),
        );
    }

    analytics.setProperties({
        application: application,
        component: analyticsConfig.component,
        browserName: bowser.name || '',
        browserVersion: bowser.version || '',
        osName: bowser.osname || '',
        osVersion: bowser.osversion || '',
        schoolId: schoolId,
        page: '',
        version: clientVersion !== undefined ? clientVersion : getClientVersion(),
    });
    return analytics;
}
